<template>
  <div>
    <portal to="dash-modal">
      <InvoiceFinancePBLModal v-if="isFinancingModalOpen" @close="toggleFinancingModal(false)" />
    </portal>

    <TitleBar :variant-class="'dashboard'">
      <template v-slot:title>
        <translate>Dashboard</translate>
      </template>
      <template v-slot:buttons>
        <OnboardingWizardLink ref="onboarding-wizard-link" />
        <PremiumCongratsLink />
        <!-- <OnboardingIntroLink :onboarding-intro="'onboarding_web_dashboard'" :open-welcome="true"/> -->
        <WidgetDropdown @add-widget="addWidget" @remove-widget="removeWidget" @reset-config="resetConfig" />
        <GekkoButton :text="$gettext('New invoice')" :href="newInvoiceUrl" icon="fa-solid fa-plus" class="mr-2" />
        <GekkoButton :text="$gettext('New cost')" :to="{ name: 'AddCost' }" icon="fa-solid fa-plus" />
      </template>
    </TitleBar>

    <!-- delete error message alert -->
    <div v-if="deleteErrorMessage" class="delete-error-message-wrapper">
      <div class="delete-error-message-warning">
        <p style="margin: 0;">{{ $gettext('Error deleting account') }}</p>
        <div>
          {{ deleteErrorMessage }}
        </div>
      </div>
    </div>
    <div class="content dashboard">
      <div class="dashboard-grid">
        <GridContainer
          v-if="initialDataLoaded"
          ref="gridcontainer"
          :key="gridKey"
          :dashboard-items="getUserWidgets"
          @save-config="debouncedSave"
          @grid-mounted="afterGridMounted"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { debounce } from '@/utils/'

import DashboardService from '@/store/services/dashboard.service.js'

import TitleBar from '@/components/TitleBar'
import GridContainer from '@/components/dashboard/GridContainer'
import OnboardingIntroLink from '@/components/ui/OnboardingIntroLink'
import OnboardingWizardLink from '@/components/ui/OnboardingWizardLink'
import GekkoButton from '@/components/GekkoButton'
import WidgetDropdown from '@/components/dashboard/ui/WidgetDropdown'
import PremiumCongratsLink from '@/components/ui/PremiumCongratsLink.vue'
import InvoiceFinancePBLModal from '@/components/invoices/ui/InvoiceFinancePBLModal'

export default {
  name: 'Dashboard',
  components: {
    TitleBar,
    GridContainer,
    OnboardingIntroLink,
    OnboardingWizardLink,
    GekkoButton,
    WidgetDropdown,
    PremiumCongratsLink,
    InvoiceFinancePBLModal,
  },
  data() {
    return {
      currentConfig: '',
      currentStoreConfig: '',
      initialDataLoaded: false,
      gridKey: 0,
      maxWidgets: 12,
      deleteErrorMessage: null,
      displayFinanceModal: false,
    }
  },
  computed: {
    ...mapGetters({
      getUserWidgets: 'dashboard/getUserWidgets',
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      getWidgetDefaults: 'dashboard/getWidgetDefaults',
      getCompanyId: 'main/getCompanyId',
      isFreePremium: 'subscriptionStore/getIsFreePremium',
      isFinancingModalOpen: 'dashboard/isFinancingModalOpen',
      accountantWidgetOnMount: 'dashboard/getAccountantWidgetOnMount',
    }),
    newInvoiceUrl() {
      return window.Urls['invoices_add_invoice_step1']()
    },
    isGrace() {
      return this.currentUser.grace_date !== null
    },
    graceDate() {
      return this.currentUser.grace_date
    },
  },
  created() {
    this.loadAvailableUsers(this.getCompanyId)
    this.loadFinanciallyActiveYears(this.getCompanyId)
    this.loadDashboardConfig({ company: this.currentCompany, accountants_portal: false }).then(() => {
      this.initialDataLoaded = true
    })
    this.debouncedSave = debounce((config) => {
      this.saveDashboard(config)
    }, 300)
    this.checkForInviteCodes()
    this.checkForFreePremium()
    if (this.$route.name === 'DashboardDeleteAccount') {
      this.checkForDeletionProcedure()
    }
  },
  methods: {
    ...mapActions({
      loadAvailableUsers: 'dashboard/loadAvailableUsers',
      loadFinanciallyActiveYears: 'dashboard/loadFinanciallyActiveYears',
      loadDashboardConfig: 'dashboard/loadDashboardConfig',
      saveDashboardConfig: 'dashboard/saveDashboardConfig',
      resetToDefault: 'dashboard/resetToDefault',
      loadUserCompanies: 'main/loadUserCompanies',
      acceptInvitation: 'notifications/acceptInvite',
      acceptAccountantInviteCode: 'notifications/acceptAccountantInviteCode',
      loadAccountantWidget: 'dashboard/loadAccountantWidget',
    }),
    ...mapMutations({
      setDashboardConfig: 'dashboard/SET_UPDATE_DASHBOARD_CONFIG',
      addStoreWidget: 'dashboard/ADD_WIDGET',
      deleteConfig: 'dashboard/DELETE_CONFIG',
      toggleFinancingModal: 'dashboard/TOGGLE_FINANCING_MODAL',
      openOnboardingWizard: 'onboarding/OPEN_ONBOARDING_WIZARD',
      setLoadAccountantWidget: 'dashboard/SET_LOAD_ACCOUNTANT_WIDGET',
    }),

    resetConfig() {
      this.resetToDefault()
      this.gridKey++
      this.$nextTick(() => {
        this.$refs.gridcontainer.saveCurrentDashboard('added')
      })
    },
    emptyDashboard() {
      this.deleteConfig()
      this.gridKey++
      this.$nextTick(() => {
        this.$refs.gridcontainer.saveCurrentDashboard('removed')
      })
    },
    getCurrentConfig() {
      this.currentConfig = this.$refs.gridcontainer.getDashboardCurrentConfig()
      this.currentStoreConfig = this.getUserWidgets
      console.log('Grid config ---------------------------------------')
      console.log(this.currentConfig)
      console.log('Store config --------------------------------------')
      console.log(this.currentStoreConfig)
    },
    addWidget(widget) {
      if (this.$refs.gridcontainer.getDashboardCurrentConfig().length < this.maxWidgets) {
        const widget_config = Object.assign({}, this.getWidgetDefaults(widget), {})
        this.addStoreWidget(widget_config)
      } else {
        this.$store.commit('setAlert', {
          title: this.$gettext('Too many widgets'),
          text: this.$gettext('The maximum number of widgets is ') + this.maxWidgets + '.',
          variant: 'warning',
          duration: 4,
        })
      }
    },
    removeWidget(widget) {
      this.$refs.gridcontainer.removeGridstackWidget(widget)
    },
    saveDashboard(config) {
      this.saveDashboardConfig(config)
    },
    checkForInviteCodes() {
      if (this.$route.query.invite_code && this.$route.query.invite_code !== '') {
        // ACCOUNTANT INVITE
        if (this.$route.query.accountant_invite) {
          let payload = {
            invite_id: this.$route.query.invite_code,
            params: {
              customer: this.currentCompany.id,
            },
          }
          this.acceptAccountantInviteCode(payload)
            .then(() => {
              this.loadUserCompanies()
              this.loadAccountantWidget()
            })
            .catch((error) => {
              let msg = this.$gettext('There was an error accepting the accountant invitation')
              if (error.status === 402) {
                msg = this.$gettext(
                  'Your accountant must upgrade their premium plan before you can accept this invitation.'
                )
              } else if (error.status === 400 && 'non_field_errors' in error.data) {
                msg = this.$gettext(
                  'You are already connected to this accountant with this company. Try a different company.'
                )
              } else if (error.status === 404) {
                msg = this.$gettext(
                  'This invitation does not exist. Please check the link or ask the sender for a new invitation.'
                )
              } else if (error.status === 409) {
                msg = this.$gettext('This invitation has already been rejected.')
              }

              this.$store.commit('setAlert', {
                title: this.$gettext('Connection rejected'),
                text: msg,
                variant: 'danger',
                duration: 4,
              })
            })
        } else {
          // TEAM INVITE
          this.acceptInvitation({ invite_code: this.$route.query.invite_code })
            .then(() => {
              this.loadUserCompanies()
              if (this.isGrace) {
                setTimeout(() => {
                  window.location.href = window.Urls['gekko_dashboard']()
                }, 1500)
              }
            })
            .catch((response) => {
              let msg = this.$gettext('An error occurred while accepting the invitation.')
              if (response.status === 409) {
                msg = this.$gettext('You are already a member of this company.')
              } else if (response.status === 404) {
                msg = this.$gettext(
                  'This invitation does not exist. Please check the link or ask the sender for a new invitation.'
                )
              }
              this.$store.commit('setAlert', {
                title: this.$gettext('Invitation Error'),
                text: msg,
                variant: 'danger',
                duration: 4,
              })
            })
        }
        if (this.$route.query.welcome === 'yes') {
          this.openOnboardingWizard()
        }
      }
    },
    checkForFreePremium() {
      if (this.$route.query.free_premium && this.$route.query.free_premium === 'true') {
        this.$router.push({ name: 'GiveFreeMonth' })
      }
    },
    checkForDeletionProcedure() {
      if (this.$route.params['uuid']) {
        if (this.currentUser.profile.deletion_procedure) {
          this.$store.commit('setAlert', {
            title: this.$gettext('Account Deletion'),
            text: this.$gettext('Your account has been marked for deletion'),
            variant: 'warning',
            duration: 3,
          })
        } else {
          DashboardService.checkDeleteToken({ delete_token: this.$route.params['uuid'] }).catch((error) => {
            if (error.status == 404) {
              this.deleteErrorMessage = this.$gettext('We could not find this token. Try copy/pasting the exact link.')
            } else if (error.status == 403) {
              this.deleteErrorMessage = this.$gettext(
                'You are logged in to the wrong account. Please log in with the account for which this link was requested.'
              )
            } else {
              this.deleteErrorMessage = this.$gettext('Unkown error')
            }
          })
        }
      }
    },
    afterGridMounted() {
      if (this.accountantWidgetOnMount) {
        this.setLoadAccountantWidget(false)
        setTimeout(() => {
          const userWidgets = this.getUserWidgets
          if (userWidgets.filter((w) => w.type === 'accountant').length < 1) {
            const widget_config = Object.assign({}, this.getWidgetDefaults('accountant'), {})
            this.addStoreWidget(widget_config)
          }
        }, 500)
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.content.dashboard {
  display: flex;
  padding: $gridGutter calc(1rem - #{$gridHalfGutter});
  padding-top: 0;
  .dashboard-grid {
    width: 100%;
  }
}

.delete-error-message-wrapper {
  padding: 1.5rem 1rem;
}

.delete-error-message-warning {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  text-align: center;
  color: #dc3545;
  background-color: rgba(#dc3545, 0.1);
  border: 1px solid #dc3545;
  border-radius: $radiusLg;
}

@media (max-width: '767px') {
  .content.dashboard {
    padding: $gridGutter 0;
    padding-top: 0;
  }
}
</style>

<style lang="scss">
.dashboard {
  .action-bar {
    flex-wrap: wrap;
    justify-content: flex-end;
    .btn {
      white-space: nowrap;
    }
    @media (max-width: '767px') {
      margin-top: 10px;
      .btn {
        margin-top: 10px;
      }
    }
  }
}
</style>
